import React, { useState, memo, useEffect } from "react";
import PropTypes from "prop-types";
import Dropzone from "react-dropzone";
import { Spinner } from "react-bootstrap";
import toast from "react-hot-toast";
import { useUploadImageMutation } from "../../store/services/settingsService";

export const isVideo = (filePath) => {
  if (!filePath) return false;
  const videoExtensions = ["mp4", "3gp", "mkv"];
  const fileExtension = filePath?.[0]?.split(".").pop()?.toLowerCase();
  return videoExtensions.includes(fileExtension);
};

// Helper function to get video duration
const getVideoDuration = (file) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };
    video.onerror = () => {
      reject("Failed to load video metadata.");
    };
    video.src = URL.createObjectURL(file);
  });
};

function DropZone({ field, setFieldValue, value, errors, requiredImageSize, allowVideos = true, ...props }) {
  const [imageFile, setImageFile] = useState({ file: null, url: null });
  const API_IMAGE_URL = process.env.REACT_APP_S3_BASE_URL;
  const [uploadFile, { isLoading: uploadFilerLoader }] =
    useUploadImageMutation();

  useEffect(() => {
    if (props.file) {
      setImageFile((prev) => ({
        ...prev,
        url: props.file?.url,
      }));
    }
  }, [props.file]);

  const getImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = function () {
        resolve({ width: this.naturalWidth, height: this.naturalHeight });
      };
      img.onerror = function () {
        reject(new Error("Failed to read image dimensions"));
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFiles = async (_files) => {
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
      "image/jpg",
      "video/mp4",
      "video/webm",
      "video/ogg",
    ];

    const validFiles = _files.filter((file) =>
      allowedTypes.includes(file.type)
    );
    const invalidFiles = _files.filter(
      (file) => !allowedTypes.includes(file.type)
    );

    if (invalidFiles.length > 0) {
      toast.error("Only image/video files are allowed (jpg, png, mp4, etc.)");
      return;
    }

    const existingCount = Array.isArray(value) ? value.length : value ? 1 : 0;
    if (existingCount + validFiles.length > 10) {
      toast.error("You can upload a maximum of 10 files.");
      return;
    }

    const maxSize = 100 * 1024 * 1024; // 100MB
    const finalFiles = [];

    for (const file of validFiles) {
      if (file.size > maxSize) {
        toast.error(`${file.name} exceeds 100MB limit.`);
        continue;
      }

      if (file.type.startsWith("video/")) {
        if (!allowVideos) {
          toast.error("Videos are not allowed here.");
          continue;
        }
        try {
          const duration = await getVideoDuration(file);
          if (duration > 30) {
            toast.error(`${file.name} is longer than 30 seconds.`);
            continue;
          }
        } catch (err) {
          toast.error(`${file.name}: ${err}`);
          continue;
        }
      }

      // Optional image dimension validation
      if (file.type.startsWith("image/") && requiredImageSize) {
        try {
          const dims = await getImageDimensions(file);
          const matches =
            dims.width === requiredImageSize.width &&
            dims.height === requiredImageSize.height;
          if (!matches) {
            toast.error(
              `The image must be exactly ${requiredImageSize.width} × ${requiredImageSize.height} pixels.`
            );
            continue;
          }
        } catch (e) {
          toast.error(`Failed to validate image size.`);
          continue;
        }
      }

      finalFiles.push(file);
    }

    if (finalFiles.length === 0) return;

    const uploadedUrls = [];

    for (const file of finalFiles) {
      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await uploadFile(formData);
        if (response?.data?.data?.url) {
          uploadedUrls.push(response.data.data.url);
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
      } catch (error) {
        toast.error(`Upload failed for ${file.name}`);
      }
    }

    if (uploadedUrls.length > 0) {
      setFieldValue(field, [...(value || []), ...uploadedUrls]);
    }
  };

  return (
    <div>
      {imageFile.url && (
        <button
          onClick={() => {
            setImageFile({ file: "", url: "" });
            props?.setFieldValue && props.setFieldValue("blogImage", "");
          }}
          type="button"
          className="border-0 bg-danger rounded-pill text-white p-0 bg-transparent position-absolute"
          style={{ top: -7, left: -5, height: 20, width: 20 }}
        >
          X
        </button>
      )}
      <Dropzone onDrop={handleFiles}>
        {({ getRootProps, getInputProps }) => (
          <div className="dropzone-single-image avatar-xl" {...getRootProps()}>
            <input
              {...getInputProps()}
              id="formrow-profile-image-Input"
              multiple={true}
            />

            {uploadFilerLoader && (
              <div
                className="dropzone-spinner"
                style={{
                  position: "absolute",
                  zIndex: 2,
                  left: 0,
                  right: 0,
                  top: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(255,255,255,0.7)",
                }}
              >
                <Spinner size="sm" style={{ color: "#7b6ee1 !important" }} />
              </div>
            )}

            {props.children ? (
              props.children
            ) : value?.includes("video") ? (
              <video
                width={"100%"}
                height={120}
                controls
                autoPlay={true}
                loop
                className="video_preview"
              >
                <source
                  src={
                    imageFile?.baseUrl ? imageFile?.baseUrl : "" + imageFile.url
                  }
                  type={"video/mp4"}
                />
              </video>
            ) : (
              <>
                {isVideo(value) ? (
                  <video
                    width={"100%"}
                    height={120}
                    controls
                    autoPlay={true}
                    loop
                    src={value}
                    className="video_preview"
                  />
                ) : (
                  <img
                    className="rounded avatar-xl"
                    alt={""}
                    src={value ? value : "/assets/img/logo-placeholder.png"}
                    width={"100%"}
                    height={"150px"}
                    style={{ objectFit: "contain", background: "#e9eef1" }}
                  />
                )}
              </>
            )}
          </div>
        )}
      </Dropzone>
    </div>
  );
}

DropZone.propTypes = {
  file: PropTypes.string,
  onChange: PropTypes.func,
};

export default memo(DropZone);
